/**
 * Per-J-code "invalid required field" detection, shared by the CPT-table
 * document-icon badge.
 *
 * Reads the *persisted* `auth_letter` values and flags the required fields that
 * hold invalid values, using the same sentinel rules the Authorisation Details
 * screen's required-field gate uses ({@link isInvalidRequiredAuthValue}). It is
 * computed from auth-on-file data without the screen's UI state so the CPT
 * table can derive it directly.
 *
 * The CPT table only surfaces this badge on the dark-green (uploaded,
 * unreviewed) icon, where — because these fields block Save — the user cannot
 * yet reach the reviewed-and-saved (light-green) state.
 *
 * @module authorisationDetails/utils/invalidRequiredFields
 */
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import { getAuthLetterNumVisitsAsString } from "./saveReviewHelpers";
import {
  isInvalidRequiredAuthValue,
  type RequiredFieldKind,
} from "./validateRequiredAuthFields";

/**
 * Field labels, in display order, for the "invalid values for" tooltip list.
 * Kept in sync with the fields the Authorisation Details screen requires.
 */
const INVALID_FIELD_LABELS = {
  authNumber: "Auth #",
  sourcePortal: "Source Portal",
  numVisits: "Num Visits",
  startDate: "Start Date",
  endDate: "End Date",
  authStatus: "Auth Status",
} as const;

/**
 * Returns the labels of the required auth-letter fields holding invalid values
 * for a J-code, based on its persisted values.
 *
 * @param jCode - J-code row with nested `auth_letter` (or undefined if the id
 *   was not found in auth-on-file)
 * @return string[]: Invalid field labels (empty when none are invalid, or when
 *   there is no J-code / no parsed auth letter to inspect — we never flag all
 *   fields just because the letter data is absent)
 */
export const getInvalidRequiredFieldNames = (
  jCode: JCodeDetails | undefined,
): string[] => {
  const authLetter = jCode?.auth_letter;
  if (!authLetter) return [];
  const checks: Array<{
    label: string;
    value: string | null | undefined;
    kind: RequiredFieldKind;
  }> = [
    {
      label: INVALID_FIELD_LABELS.authNumber,
      value: authLetter?.auth_letter_auth_number,
      kind: "text",
    },
    {
      label: INVALID_FIELD_LABELS.sourcePortal,
      value: authLetter?.source_portal,
      kind: "text",
    },
    {
      label: INVALID_FIELD_LABELS.numVisits,
      value: (jCode && getAuthLetterNumVisitsAsString(jCode)) || undefined,
      kind: "count",
    },
    {
      label: INVALID_FIELD_LABELS.startDate,
      value: authLetter?.auth_letter_start_date,
      kind: "text",
    },
    {
      label: INVALID_FIELD_LABELS.endDate,
      value: authLetter?.auth_letter_end_date,
      kind: "text",
    },
    {
      label: INVALID_FIELD_LABELS.authStatus,
      value: authLetter?.auth_letter_auth_status,
      kind: "text",
    },
  ];
  return checks
    .filter((c) => isInvalidRequiredAuthValue(c.value, c.kind))
    .map((c) => c.label);
};
